import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
  Modifier
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "../../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./SongEditor.scss";
import { DEFAULT_ROW } from "../../../@core/constants";

interface SongEditorProps {
  row: string;
  onRowChange: (row: string) => void;
}

type EditorStateInterface = [
  editorState: EditorState,
  setEditorState: (editorState: EditorState) => void
];

const SongEditor = forwardRef(function SongEditor(
  { row, onRowChange }: SongEditorProps,
  ref
) {
  const [editorState, setEditorState]: EditorStateInterface =
    useState<EditorState>(
      EditorState.createWithContent(
        convertFromRaw(JSON.parse(row || DEFAULT_ROW))
      )
    );

  const onEditorStateChange = (newState: EditorState) => {
    setEditorState(newState);
    const row = convertToRaw(newState.getCurrentContent());
    onRowChange(JSON.stringify(row));
  };

  const handlePastedText = (text: string): boolean => {
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const plainContent = ContentState.createFromText(text);
    const newContent = Modifier.replaceWithFragment(
      currentContent,
      selection,
      plainContent.getBlockMap()
    );
    const newState = EditorState.push(editorState, newContent, "insert-fragment");
    onEditorStateChange(newState);
    return true;
  };

  const updateEditorContent = (newContent: string) => {
    // Remove both "Normal" and "Font" at the start of the content
    let cleanedContent = newContent.replace(
      /^(Normal|Font)\s*(Normal|Font)?\s*/i,
      ""
    );
    // Remove extra empty lines (consecutive empty lines will be reduced to one)
    cleanedContent = cleanedContent.replace(/(\n{2,})/g, "\n\n");

    // Now create the content state and editor state
    const newContentState = ContentState.createFromText(cleanedContent);
    const newEditorState = EditorState.createWithContent(newContentState);

    // Update the editor state
    setEditorState(EditorState.moveFocusToEnd(newEditorState));
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = wrapperRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;

    const getToolbar = () => container.querySelector(".rdw-editor-toolbar") as HTMLElement | null;
    const getEditorWrapper = () => container.querySelector(".wrapper") as HTMLElement | null;

    const applyFixed = () => {
      const toolbar = getToolbar();
      const editorWrapper = getEditorWrapper();
      if (!toolbar) return;
      const width = container.getBoundingClientRect().width;
      const height = toolbar.getBoundingClientRect().height;
      toolbar.style.setProperty("--toolbar-width", `${width}px`);
      toolbar.classList.add("toolbar--fixed");
      if (editorWrapper) editorWrapper.style.paddingTop = `${height}px`;
    };

    const removeFixed = () => {
      const toolbar = getToolbar();
      const editorWrapper = getEditorWrapper();
      if (!toolbar) return;
      toolbar.classList.remove("toolbar--fixed");
      toolbar.style.removeProperty("--toolbar-width");
      if (editorWrapper) editorWrapper.style.paddingTop = "";
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) applyFixed();
        else removeFixed();
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);

    const handleResize = () => {
      const toolbar = getToolbar();
      if (!toolbar?.classList.contains("toolbar--fixed")) return;
      toolbar.style.setProperty("--toolbar-width", `${container.getBoundingClientRect().width}px`);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      removeFixed();
    };
  }, []);

  // ✅ Expose methods to the parent via ref
  useImperativeHandle(ref, () => ({
    //resetEditor: () => setEditorState(EditorState.createEmpty()), // Clears content
    setContent: (text: string) => updateEditorContent(text), // Update content
    focusEditor: () => setEditorState(EditorState.moveFocusToEnd(editorState)) // Move focus
  }));

  return (
    <div className="song-editor" ref={wrapperRef}>
      <div ref={sentinelRef} className="song-editor-sentinel" />
      <Editor
        wrapperClassName="wrapper"
        editorClassName="editor"
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        handlePastedText={handlePastedText}
      />
    </div>
  );
});

export default SongEditor;
